import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchAllUsers } from "../../../../redux/authSlice";
import { listenToCleaningHostReservations } from "../../../../redux/cleaningReservationSlice";
import { listenToCleaningData } from "../../../../redux/cleaningSlice";

/* ── green tokens ── */
const G = {
  green:      "#16a34a",
  greenDark:  "#14532d",
  greenLight: "#dcfce7",
  border:     "#bbf7d0",
  border2:    "#86efac",
  muted:      "#6b7280",
  text:       "#14532d",
  surface:    "#ffffff",
  bg:         "#f0fdf4",
};

const STATUS_GROUPS = [
  { key: "currentlyHosting", label: "Ongoing"      },
  { key: "arrivingSoon",     label: "Arriving Soon" },
  { key: "upcoming",         label: "Upcoming"      },
  { key: "checkingOut",      label: "Ending Today"  },
  { key: "completed",        label: "Completed"     },
  { key: "confirmed",        label: "Confirmed"     },
  { key: "cancelled",        label: "Cancelled"     },
];

const CleaningToday: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const host   = useSelector((s: RootState) => s.auth.user);
  const hostId = host?.userId || (host as any)?.uid;

  const reservations = useSelector((s: RootState) => s.cleaningReservations.hostReservations);
  const users        = useSelector((s: RootState) => s.auth.allUsers);
  const listings     = useSelector((s: RootState) => s.cleaning.listings);

  const [fetchedListings, setFetchedListings] = useState<string[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [selectedStatus,  setSelectedStatus]  = useState("currentlyHosting");

  useEffect(() => {
    if (hostId) {
      dispatch(listenToCleaningHostReservations(hostId));
      dispatch(fetchAllUsers());
    }
  }, [dispatch, hostId]);

  useEffect(() => {
    reservations.forEach((res) => {
      if (!fetchedListings.includes(res.listingId)) {
        dispatch(listenToCleaningData(res.listingId));
        setFetchedListings((prev) => [...prev, res.listingId]);
      }
    });
    setLoading(false);
  }, [reservations, fetchedListings, dispatch]);

  /* ── group reservations ── */
  const grouped = useMemo(() => {
    const g: Record<string, typeof reservations> = {
      confirmed: [], currentlyHosting: [], arrivingSoon: [],
      upcoming: [], checkingOut: [], completed: [], cancelled: [],
    };
    reservations.forEach((res) => {
      if (res.isCancelled)                         g.cancelled.push(res);
      else if (res.isCheckingIn || res.isOngoing)  g.currentlyHosting.push(res);
      else if (res.isCheckingOut)                  g.checkingOut.push(res);
      else if (res.isArrivingSoon)                 g.arrivingSoon.push(res);
      else if (res.isUpcoming)                     g.upcoming.push(res);
      else if (res.isCompleted)                    g.completed.push(res);
      else if (res.isConfirmed)                    g.confirmed.push(res);
    });
    return g;
  }, [reservations]);

  const filtered = grouped[selectedStatus] || [];

  return (
    <div style={s.wrapper}>

      {/* ── filter pills ── */}
      <div style={s.filterRow}>
        {STATUS_GROUPS.map(({ key, label }) => (
          <button
            key={key}
            style={{
              ...s.filterBtn,
              ...(selectedStatus === key ? s.filterBtnActive : {}),
            }}
            onClick={() => setSelectedStatus(key)}
          >
            {label}
            <span style={{
              ...s.countBadge,
              background: selectedStatus === key ? "rgba(255,255,255,.25)" : G.greenLight,
              color:      selectedStatus === key ? "#fff" : G.green,
            }}>
              {grouped[key].length}
            </span>
          </button>
        ))}
      </div>

      {/* ── reservation cards ── */}
      {loading ? (
        <p style={{ color: G.muted, padding: 20 }}>Loading…</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: G.muted }}>
          <span style={{ fontSize: 40 }}>🧹</span>
          <p style={{ marginTop: 10, fontWeight: 600 }}>
            No reservations in "{STATUS_GROUPS.find((g) => g.key === selectedStatus)?.label}"
          </p>
        </div>
      ) : (
        <div style={s.cardList}>
          {filtered.map((res) => {
            const guest      = users.find((u: any) => u.userId === res.guestId);
            const listing    = listings.find((l: any) => l.listingId === res.listingId);
            const listingImg = listing?.photos?.[0] || "/fallback.jpg";
            const canReview  = !!res.isCheckingOut || !!res.isCompleted;

            // ── disable cancel once service has started or is ending (mirrors CleaningReservations) ──
            const disableCancel = res.hasCheckedIn || res.isOngoing || res.isCheckingOut;

            const guestInitials = `${guest?.firstName?.[0] || "C"}${guest?.lastName?.[0] || ""}`.toUpperCase();

            const checkIn  = res.checkIn  ? new Date(res.checkIn).toLocaleDateString("en-GB",  { day: "numeric", month: "short" }) : "—";
            const checkOut = res.checkOut ? new Date(res.checkOut).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—";

            return (
              <div key={res.id} style={s.card}>

                {/* guest avatar → /aboutme/@username (matches ShortRequests pattern) */}
                {guest?.profilePicture ? (
                  <img
                    src={guest.profilePicture}
                    style={s.profileImg}
                    alt="guest"
                    onClick={() => navigate(`/aboutme/@${guest?.username || res.guestId}`)}
                  />
                ) : (
                  <div
                    style={s.initialsCircle}
                    onClick={() => navigate(`/aboutme/@${guest?.username || res.guestId}`)}
                  >
                    {guestInitials}
                  </div>
                )}

                <span style={s.arrow}>→</span>

                {/* listing image */}
                <img
                  src={listingImg}
                  style={s.listingImg}
                  alt="listing"
                  onClick={() => navigate(`/cleaningdetails/${res.listingId}`)}
                />

                {/* dates + info */}
                <div style={s.infoBlock}>
                  <p style={s.guestName}>{guest?.firstName || "Client"} {guest?.lastName || ""}</p>
                  <p style={s.dateText}>{checkIn} → {checkOut}</p>
                  <p style={s.earnings}>€{(res.hostProfit || 0).toFixed(2)} profit</p>
                </div>

                {/* review + cancel buttons */}
                <div style={s.rightBlock}>
                  <button
                    onClick={() => canReview && navigate(`/cleaninghostrating/${hostId}/${res.listingId}/${res.id}`)}
                    disabled={!canReview}
                    style={{ ...s.reviewBtn, ...(canReview ? s.reviewEnabled : s.reviewDisabled) }}
                  >
                    Review Client
                  </button>

                  <button
                    disabled={disableCancel}
                    style={{
                      ...s.cancelSmallBtn,
                      ...(disableCancel ? s.cancelDisabled : {}),
                    }}
                    onClick={() =>
                      !disableCancel &&
                      navigate(`/hostcancelcleaningbooking/${hostId}/${res.listingId}/${res.id}`)
                    }
                  >
                    Cancel
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CleaningToday;

/* ── styles ── */
const s: Record<string, React.CSSProperties> = {
  wrapper: {
    backgroundColor: G.bg,
    minHeight: "100vh",
    paddingBottom: 80,
  },

  filterRow: {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    padding: "14px 16px",
    scrollbarWidth: "none",
    WebkitOverflowScrolling: "touch",
  },
  filterBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 999,
    backgroundColor: G.surface,
    border: `1px solid ${G.border}`,
    color: G.text,
    fontWeight: 600,
    cursor: "pointer",
    fontSize: 12,
    whiteSpace: "nowrap",
    flexShrink: 0,
    transition: "all .18s",
  },
  filterBtnActive: {
    backgroundColor: G.greenDark,
    borderColor:     G.greenDark,
    color:           "#fff",
  },
  countBadge: {
    fontSize: 10,
    fontWeight: 800,
    borderRadius: 999,
    padding: "1px 6px",
    minWidth: 18,
    textAlign: "center" as const,
  },

  cardList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
    padding: "0 16px",
  },
  card: {
    background: G.surface,
    border: `1px solid ${G.border}`,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 16px",
    width: "100%",
    maxWidth: 680,
    flexWrap: "wrap" as const,
    boxShadow: "0 4px 12px rgba(22,163,74,.07)",
  },

  profileImg:     { width: 48, height: 48, borderRadius: "50%", objectFit: "cover" as const, cursor: "pointer", border: `2px solid ${G.green}`, flexShrink: 0 },
  initialsCircle: { width: 48, height: 48, borderRadius: "50%", background: `linear-gradient(135deg, ${G.green}, ${G.greenDark})`, color: "#fff", display: "flex", justifyContent: "center", alignItems: "center", fontWeight: 700, fontSize: 15, cursor: "pointer", flexShrink: 0 },
  arrow:          { fontSize: 14, color: G.border2, flexShrink: 0 },
  listingImg:     { width: 48, height: 48, borderRadius: 10, objectFit: "cover" as const, cursor: "pointer", border: `2px solid ${G.border}`, flexShrink: 0 },

  infoBlock: { flex: 1, display: "flex", flexDirection: "column" as const, gap: 2, minWidth: 120 },
  guestName: { fontSize: 13, fontWeight: 700, color: G.text, margin: 0 },
  dateText:  { fontSize: 12, color: G.muted, margin: 0 },
  earnings:  { fontSize: 12, fontWeight: 700, color: G.green, margin: 0 },

  rightBlock:     { display: "flex", flexDirection: "column" as const, gap: 6, alignItems: "flex-end", flexShrink: 0 },
  reviewBtn:      { padding: "7px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700, border: "none", transition: "all .18s", cursor: "pointer" },
  reviewEnabled:  { background: G.greenDark, color: "#fff" },
  reviewDisabled: { background: "#e5e7eb", color: "#9ca3af", cursor: "not-allowed" as const },

  cancelSmallBtn: {
    padding: "5px 12px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    border: `1px solid ${G.border}`,
    background: "transparent",
    color: G.muted,
    cursor: "pointer",
    transition: "all .18s",
  },
  cancelDisabled: {
    background: "#e5e7eb",
    color: "#9ca3af",
    cursor: "not-allowed" as const,
    borderColor: "#e5e7eb",
  },
};